import { create } from 'zustand';
import { LineItemTypeOptions, TAX_CODE_OTHER_EXEMPT } from '../types/OcrTypes';
import { InvoiceOcrLineItemsSchema, InvoiceOcrLineItemsSchemaWithGlcodeIDRequired, InvoiceOcrLineItemsSchemaWithGlcodeIDRequiredExceptTaxItem, } from '../logic/OcrInvoiceValidator';
import { OfflineOcrDataPageFunctions } from '../logic/OfflineOcrDataPageFunctions';
import { getLineItemFieldByIndex, getLineItemFormErrors } from "../logic/OcrUtils";
import { getEmptyInvoice, updateTotals } from '../logic/OcrInvoiceReducer';
import { TaxCalculationSettings } from '../network/OcrLoadNetwork';
import { consolidateByGLCode, valueToFixedNumber } from '../logic/OcrInvoiceHelpers';
export const FORM_ERRORS_INITIAL_STATE = {
    requiredFields: {
        errorLineItemRefs: [],
        errorFields: {},
        totalCount: 0,
    },
    lowConfidenceFields: {
        errorLineItemRefs: [],
        errorFields: {},
        totalCount: 0,
    }
};
export const INVOICE_TOP_LEVEL_DATA_ERRORS_INITIAL_STATE = {
    errorFields: {},
    totalCount: 0,
};
export const INVOICE_INITIAL_STATE = getEmptyInvoice();
export const INVOICES_INITIAL_STATE = {
    hasNextPage: false,
    nextCursor: '',
    results: [],
    total: 0
};
export const ocrFunctions = new OfflineOcrDataPageFunctions();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useOcrInvoiceStore = create((set, get) => ({
    showFocusPanel: false,
    setShowFocusPanel: (show) => set(() => ({ showFocusPanel: show })),
    focusType: "required" /* LineItemFocusType.Required */,
    setFocusType: (focusType) => set((state) => {
        get().validateForm();
        return (Object.assign(Object.assign({}, state), { focusType }));
    }),
    draftInvoice: INVOICE_INITIAL_STATE,
    setDraftInvoice: (invoice) => set(() => {
        get().setFormErrors({
            lowConfidenceFields: invoice === null || invoice === void 0 ? void 0 : invoice.lowConfidenceFields,
            requiredFields: invoice === null || invoice === void 0 ? void 0 : invoice.missingRequiredFields,
        });
        return ({ draftInvoice: invoice });
    }),
    formErrors: FORM_ERRORS_INITIAL_STATE,
    setFormErrors: (errors) => set(() => ({ formErrors: errors })),
    addFormLineItem: (type = LineItemTypeOptions.PRODUCT) => set((state) => {
        const emptyLineItem = ocrFunctions.makeEmptyLineItem(type);
        return {
            draftInvoice: Object.assign(Object.assign({}, state.draftInvoice), { lineItems: [...state.draftInvoice.lineItems, emptyLineItem] })
        };
    }),
    addChartOfAccountDetailsItem: () => set((state) => {
        const emptyLineItem = ocrFunctions.makeEmptyLineItem('product');
        return {
            pendingChartOfAccountDetails: [
                ...state.pendingChartOfAccountDetails,
                emptyLineItem
            ]
        };
    }),
    removeLineItem: (reactKey) => set((state) => {
        var _a, _b;
        const { draftInvoice } = state;
        const index = (_a = state.draftInvoice) === null || _a === void 0 ? void 0 : _a.lineItems.findIndex((i) => i.reactKey === reactKey);
        const newLineItems = (_b = state.draftInvoice) === null || _b === void 0 ? void 0 : _b.lineItems.filter((i) => i.reactKey !== reactKey);
        get().removeLowConfidenceErrorFieldByIndex(index);
        const updatedInvoice = Object.assign(Object.assign({}, draftInvoice), { lineItems: newLineItems });
        updateTotals(updatedInvoice);
        return {
            draftInvoice: updatedInvoice
        };
    }),
    removeChartOfAccountDetailsItem: (reactKey) => set((state) => {
        const { pendingChartOfAccountDetails } = state;
        return {
            pendingChartOfAccountDetails: pendingChartOfAccountDetails.filter((i) => i.reactKey !== reactKey)
        };
    }),
    resetStore: () => set(() => ({
        showFocusPanel: false,
        focusType: "required" /* LineItemFocusType.Required */,
        formErrors: FORM_ERRORS_INITIAL_STATE,
        invoices: INVOICES_INITIAL_STATE,
        draftInvoice: INVOICE_INITIAL_STATE,
        errorLineItemFieldsCount: 0,
        errorLineItemRefs: [],
    })),
    errorLineItemFieldsCount: 0,
    setErrorLineItemFieldsCount: (count) => set(() => ({ errorLineItemFieldsCount: count })),
    errorLineItemRefs: [],
    setErrorLineItemRefs: (refs) => set((state) => {
        if (state.focusType === "required" /* LineItemFocusType.Required */) {
            return ({ formErrors: Object.assign(Object.assign({}, state.formErrors), { requiredFields: Object.assign(Object.assign({}, state.formErrors.requiredFields), { errorLineItemRefs: refs }) }) });
        }
        return ({ formErrors: Object.assign(Object.assign({}, state.formErrors), { lowConfidenceFields: Object.assign(Object.assign({}, state.formErrors.requiredFields), { errorLineItemRefs: refs }) }) });
    }),
    validateForm: () => set((state) => {
        var _a, _b, _c, _d;
        const invoicePrimaryData = get().getInvoiceLineItemsPrimaryData();
        const { draftInvoice } = state;
        const isCustomTax = ((_a = draftInvoice === null || draftInvoice === void 0 ? void 0 : draftInvoice.topLevelData) === null || _a === void 0 ? void 0 : _a.taxCalculationSettings) === TaxCalculationSettings.CUSTOM;
        // Gl code is required for all line items, except tax items when custom tax is enabled
        const validationSchema = (draftInvoice === null || draftInvoice === void 0 ? void 0 : draftInvoice.isQBOEnabled) ?
            isCustomTax ? InvoiceOcrLineItemsSchemaWithGlcodeIDRequired : InvoiceOcrLineItemsSchemaWithGlcodeIDRequiredExceptTaxItem
            : InvoiceOcrLineItemsSchema;
        const result = validationSchema.safeParse(invoicePrimaryData);
        const { errorLineItemFieldsCount: lowConfidenceFieldsCount, errorLineItemRefs: lowConfidenceLineItemRefs } = getLineItemFormErrors((_c = (_b = get().formErrors) === null || _b === void 0 ? void 0 : _b.lowConfidenceFields) === null || _c === void 0 ? void 0 : _c.errorFields);
        let requiredFields = {
            errorFields: {},
            totalCount: 0,
        };
        if (result.success === false) {
            const formRequiredErrors = result.error.format();
            const { errorLineItemFieldsCount: requiredFieldsCount, errorLineItemRefs } = getLineItemFormErrors(formRequiredErrors);
            requiredFields = {
                errorFields: formRequiredErrors,
                totalCount: requiredFieldsCount,
                errorLineItemRefs
            };
        }
        const lowConfidenceFields = Object.assign(Object.assign({}, state.formErrors.lowConfidenceFields), { errorFields: (_d = state.formErrors.lowConfidenceFields) === null || _d === void 0 ? void 0 : _d.errorFields, totalCount: lowConfidenceFieldsCount, errorLineItemRefs: lowConfidenceLineItemRefs });
        return {
            formErrors: Object.assign(Object.assign({}, state.formErrors), { requiredFields,
                lowConfidenceFields })
        };
    }),
    validateTopLevelData: () => set((state) => {
        const invoiceTopLevelDataErrors = JSON.parse(JSON.stringify(INVOICE_TOP_LEVEL_DATA_ERRORS_INITIAL_STATE));
        const { topLevelData } = state.draftInvoice;
        const invoiceTotal = valueToFixedNumber(topLevelData.total);
        if (invoiceTotal < 0) {
            invoiceTopLevelDataErrors.errorFields.total = 'Invoice total cannot be negative';
            invoiceTopLevelDataErrors.totalCount++;
        }
        return {
            invoiceTopLevelDataErrors
        };
    }),
    invoiceTopLevelDataErrors: Object.assign({}, INVOICE_TOP_LEVEL_DATA_ERRORS_INITIAL_STATE),
    setInvoiceTopLevelDataErrors: (errors) => set(() => ({ invoiceTopLevelDataErrors: errors })),
    setErrorsByType: () => set((state) => {
        var _a, _b;
        const { errorLineItemFieldsCount: lowConfidenceFieldsCount, errorLineItemRefs: lowConfidenceLineItemRefs } = getLineItemFormErrors((_a = state.formErrors.lowConfidenceFields) === null || _a === void 0 ? void 0 : _a.errorFields);
        const { errorLineItemFieldsCount: requiredFieldsCount, errorLineItemRefs: requiredLineItemRefs } = getLineItemFormErrors((_b = state.formErrors.requiredFields) === null || _b === void 0 ? void 0 : _b.errorFields);
        return {
            formErrors: Object.assign(Object.assign({}, state.formErrors), { requiredFields: Object.assign(Object.assign({}, state.formErrors.requiredFields), { errorLineItemRefs: requiredLineItemRefs, totalCount: requiredFieldsCount }), lowConfidenceFields: Object.assign(Object.assign({}, state.formErrors.lowConfidenceFields), { errorLineItemRefs: lowConfidenceLineItemRefs, totalCount: lowConfidenceFieldsCount }) })
        };
    }),
    getErrorsCountByTypeSelected: () => {
        var _a, _b, _c, _d;
        if (get().focusType === "required" /* LineItemFocusType.Required */) {
            return (_b = (_a = get().formErrors.requiredFields) === null || _a === void 0 ? void 0 : _a.totalCount) !== null && _b !== void 0 ? _b : 0;
        }
        return (_d = (_c = get().formErrors.lowConfidenceFields) === null || _c === void 0 ? void 0 : _c.totalCount) !== null && _d !== void 0 ? _d : 0;
    },
    clearLowConfidenceFieldByIndex: (index, field) => set(() => {
        var _a, _b;
        const itemRefId = getLineItemFieldByIndex(index, field);
        const { formErrors, focusType } = get();
        const isLowConfidenceFocus = focusType === "low-confidence" /* LineItemFocusType.LowConfidence */;
        const isLowConfidenceInRefs = (_b = (_a = formErrors.lowConfidenceFields) === null || _a === void 0 ? void 0 : _a.errorLineItemRefs) === null || _b === void 0 ? void 0 : _b.includes(itemRefId);
        const shouldClearField = isLowConfidenceFocus && isLowConfidenceInRefs;
        if (shouldClearField) {
            return {
                formErrors: Object.assign(Object.assign({}, formErrors), { lowConfidenceFields: Object.assign(Object.assign({}, formErrors.lowConfidenceFields), { errorFields: Object.assign(Object.assign({}, formErrors.lowConfidenceFields.errorFields), { [index]: Object.assign(Object.assign({}, formErrors.lowConfidenceFields.errorFields[index]), { [field]: null }) }) }) })
            };
        }
        return {
            formErrors,
        };
    }),
    unshiftEmptyLowConfidenceErrorField: () => set((state) => {
        const { lowConfidenceFields } = get().formErrors;
        const newLowLineItems = {};
        Object.keys(lowConfidenceFields.errorFields).forEach((key) => {
            const newKey = Number(key) + 1;
            newLowLineItems[newKey] = lowConfidenceFields.errorFields[key];
        });
        newLowLineItems[0] = {};
        return {
            formErrors: Object.assign(Object.assign({}, state.formErrors), { lowConfidenceFields: Object.assign(Object.assign({}, state.formErrors.lowConfidenceFields), { errorFields: newLowLineItems }) })
        };
    }),
    removeLowConfidenceErrorFieldByIndex: (index) => set((state) => {
        const { lowConfidenceFields } = get().formErrors;
        const newLowLineItems = {};
        Object.keys(lowConfidenceFields.errorFields).forEach((key) => {
            const keyIndex = Number(key);
            if (keyIndex !== index) {
                const newKey = keyIndex > index ? keyIndex - 1 : keyIndex;
                newLowLineItems[newKey] = lowConfidenceFields.errorFields[key];
            }
        });
        return {
            formErrors: Object.assign(Object.assign({}, state.formErrors), { lowConfidenceFields: Object.assign(Object.assign({}, state.formErrors.lowConfidenceFields), { errorFields: newLowLineItems }) })
        };
    }),
    updateInvoiceTopLevelData: (key, value) => set((state) => {
        const { draftInvoice } = state;
        return {
            draftInvoice: Object.assign(Object.assign({}, draftInvoice), { topLevelData: Object.assign(Object.assign({}, draftInvoice.topLevelData), { [key]: value }) })
        };
    }),
    invoices: INVOICES_INITIAL_STATE,
    setInvoices: (invoices) => set(() => ({ invoices })),
    getOcrMatchedLineItemByKey: (key, isChartOfAccountDetails) => {
        const { draftInvoice, pendingChartOfAccountDetails } = get();
        const source = isChartOfAccountDetails ? pendingChartOfAccountDetails : draftInvoice.lineItems;
        const index = source.findIndex((lineItem) => lineItem.reactKey === key);
        return { lineItem: source[index], index };
    },
    getLineItemSubtotal: (lineItem) => ocrFunctions.getLineItemSubtotal(lineItem),
    updateLineItemPrimaryData: (reactKey, lineItemKey, value) => set((state) => {
        const { getOcrMatchedLineItemByKey, clearLowConfidenceFieldByIndex } = get();
        const { draftInvoice } = state;
        // get the line item by react key
        const { lineItem, index } = getOcrMatchedLineItemByKey(reactKey);
        // check and update state.formErrors
        clearLowConfidenceFieldByIndex(index, lineItemKey);
        const newLineItem = Object.assign({}, lineItem);
        newLineItem.primaryItemData[lineItemKey] = value;
        // in scope of this function we are updating: tax, total, matchResult
        ocrFunctions.updateLineItemTax(newLineItem, newLineItem.primaryItemData.tax.percent / 100);
        if (lineItemKey === 'tax') {
            newLineItem.primaryItemData.taxIsSet = true;
        }
        if (lineItemKey === 'taxCode' && value === TAX_CODE_OTHER_EXEMPT.value) {
            newLineItem.primaryItemData.tax = {
                dollarAmount: 0,
                percent: 0
            };
        }
        const updatedInvoice = Object.assign(Object.assign({}, draftInvoice), { lineItems: draftInvoice.lineItems.map((item) => {
                if (item.reactKey === reactKey) {
                    return newLineItem;
                }
                return item;
            }) });
        // updates the top level totals of the invoices
        updateTotals(updatedInvoice);
        return {
            draftInvoice: updatedInvoice
        };
    }),
    updateChartOfAccountDetailsItem: (reactKey, lineItemKey, value) => set((state) => {
        const { getOcrMatchedLineItemByKey } = get();
        const { pendingChartOfAccountDetails } = state;
        // get the line item by react key
        const { lineItem } = getOcrMatchedLineItemByKey(reactKey, true);
        const newLineItem = Object.assign({}, lineItem);
        newLineItem.primaryItemData[lineItemKey] = value;
        const updatedChartOfAccountDetails = pendingChartOfAccountDetails.map((item) => {
            if (item.reactKey === reactKey) {
                return newLineItem;
            }
            return item;
        });
        return {
            pendingChartOfAccountDetails: updatedChartOfAccountDetails
        };
    }),
    updateLinkedCatalogData: (reactKey, lineItemData, taxCode) => set((state) => {
        const { draftInvoice } = state;
        // get the line item by react key
        const { lineItem } = get().getOcrMatchedLineItemByKey(reactKey);
        const newLineItem = Object.assign({}, lineItem);
        newLineItem.linkedCatalogData = {
            id: lineItemData === null || lineItemData === void 0 ? void 0 : lineItemData.id,
            description: lineItemData === null || lineItemData === void 0 ? void 0 : lineItemData.description,
            availability: lineItemData === null || lineItemData === void 0 ? void 0 : lineItemData.availability,
            sku: lineItemData === null || lineItemData === void 0 ? void 0 : lineItemData.sku,
            quantity: lineItemData === null || lineItemData === void 0 ? void 0 : lineItemData.quantity,
            unit: lineItemData === null || lineItemData === void 0 ? void 0 : lineItemData.unit,
            prices: lineItemData.prices.map(({ pricePerUnit, priceListID, id, available }) => ({ pricePerUnit, priceListID, id, available })),
            taxCode: taxCode,
        };
        const updatedInvoice = Object.assign(Object.assign({}, draftInvoice), { lineItems: draftInvoice.lineItems.map((item) => {
                if (item.reactKey === reactKey) {
                    return newLineItem;
                }
                return item;
            }) });
        return {
            draftInvoice: updatedInvoice
        };
    }),
    getInvoiceLineItemsPrimaryData: () => {
        var _a, _b;
        const { draftInvoice } = get();
        return (_b = (_a = draftInvoice.lineItems) === null || _a === void 0 ? void 0 : _a.map((lineItem) => lineItem.primaryItemData)) !== null && _b !== void 0 ? _b : [];
    },
    getNewLineItems: () => {
        const { draftInvoice } = get();
        return draftInvoice.lineItems.filter((lineItem) => { var _a; return !lineItem.linkedCatalogData && !((_a = lineItem.primaryItemData) === null || _a === void 0 ? void 0 : _a.packageID); });
    },
    isAutoCalculateOn: true,
    setIsAutoCalculateOn: (setting) => set(() => ({ isAutoCalculateOn: setting })),
    didAutoCalculateJustRun: false,
    setDidAutoCalculateJustRun: (setting) => set(() => ({ didAutoCalculateJustRun: setting })),
    pendingChartOfAccountDetails: INVOICE_INITIAL_STATE.chartOfAccountDetails,
    setPendingChartOfAccountDetails: (details) => set(() => ({ pendingChartOfAccountDetails: details })),
    autoCalculateChartOfAccountDetails: () => set(() => {
        const { draftInvoice } = get();
        const consolidatedChartOfAccountDetails = consolidateByGLCode(draftInvoice.lineItems, draftInvoice.taxCodes);
        return { pendingChartOfAccountDetails: consolidatedChartOfAccountDetails,
            draftInvoice: Object.assign(Object.assign({}, draftInvoice), { chartOfAccountDetails: consolidatedChartOfAccountDetails }),
            didAutoCalculateJustRun: true };
    }),
    saveChartOfAccountDetails: () => set(() => {
        const { draftInvoice, pendingChartOfAccountDetails } = get();
        const updatedDraftInvoice = { draftInvoice: Object.assign(Object.assign({}, draftInvoice), { chartOfAccountDetails: structuredClone(pendingChartOfAccountDetails) }) };
        return updatedDraftInvoice;
    }),
}));
//# sourceMappingURL=OcrInvoiceStore.js.map